/* src/components/game/AudioLetterDisplay.js */
import React, { useMemo } from 'react';

/**
 * Enhanced AudioLetterDisplay component that shows a visual history of audio stimuli 
 * for novice players when helpers are enabled.
 * Uses the updated design language to match the redesigned PositionGrid.
 * 
 * Props:
 * - sequence: {Array<object>} The game sequence array
 * - currentTrial: {number} The index of the current trial
 * - nBack: {number} The current n-back level
 * - difficulty: {number} The current game difficulty (0.0 - 0.95)
 * - audioFeedback: {'correct'|'incorrect'|'acknowledged'|null} Feedback state
 * - showNoviceHelp: {boolean} Whether to show helper numbers/letters
 * - isReplayMode: {boolean} Whether component is in replay viewing mode
 * - isTutorialMode: {boolean} Whether component is in tutorial mode
 * - activeLetter: {string|null} Letter to highlight (Replay/Tutorial mode)
 * - feedbackState: {'correct'|'incorrect'|'missed'|null} Feedback state (Replay/Tutorial)
 */
const AudioLetterDisplay = ({
  sequence,
  currentTrial,
  nBack,
  difficulty,
  audioFeedback,
  showNoviceHelp,
  isReplayMode = false,
  isTutorialMode = false,
  activeLetter = null,
  feedbackState = null
}) => {
  // Only show this component if novice help is enabled or in replay/tutorial mode
  if (!showNoviceHelp && !isReplayMode && !isTutorialMode) {
    return null;
  }

  // Calculate which letters to display
  const lettersToDisplay = useMemo(() => {
    if (!sequence || currentTrial < 0 || !Array.isArray(sequence)) {
      return [];
    }

    // Calculate how many letters to show (n-back + 1 for current)
    const numToShow = nBack + 1;
    
    // Get letters from the sequence, starting with current letter and going backwards
    const letters = [];
    for (let i = 0; i < numToShow; i++) {
      const targetIndex = currentTrial - i;
      if (targetIndex >= 0 && targetIndex < sequence.length) {
        letters.push({
          letter: sequence[targetIndex].letter,
          index: i,
          trialIndex: targetIndex
        });
      } else {
        // Fill with placeholders for unavailable positions
        letters.push({
          letter: null,
          index: i,
          trialIndex: -1
        });
      }
    }
    
    return letters;
  }, [sequence, currentTrial, nBack]);

  // Determine which index to highlight (if any)
  const activeIndex = isReplayMode || isTutorialMode 
    ? lettersToDisplay.findIndex(item => item.letter === activeLetter)
    : 0; // In live mode, always highlight the current letter (index 0)

  // Get feedback color based on mode and state
  const getFeedbackColor = (index) => {
    if (index !== activeIndex) return null;
    
    if (isReplayMode || isTutorialMode) {
      if (feedbackState === 'correct') return 'bg-green-50 border-green-300 text-green-700';
      if (feedbackState === 'incorrect' || feedbackState === 'missed') return 'bg-red-50 border-red-300 text-red-700';
      return 'bg-indigo-50 border-indigo-300 text-indigo-700';
    } else {
      // Live mode feedback
      if (index === 0 && audioFeedback) {
        if (audioFeedback === 'correct') return 'bg-green-50 border-green-300 text-green-700';
        if (audioFeedback === 'incorrect') return 'bg-red-50 border-red-300 text-red-700';
      }
      return 'bg-indigo-50 border-indigo-300 text-indigo-700';
    }
  };

  // Helper to get n-position label
  const getNPositionLabel = (index) => {
    if (index === 0) return 'Current';
    if (index === nBack) return 'N Match';
    return `N-${index}`;
  };

  return (
    <div className="flex flex-col space-y-1.5 w-full sm:w-24 md:w-28 lg:w-32 fade-in">
      {/* Apply animation keyframes */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-5px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .fade-in {
            animation: fadeIn 0.2s ease-out forwards;
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          .letter-pulse {
            animation: pulse 0.3s ease-in-out;
          }
          @keyframes subtleGlow {
            0%, 100% { box-shadow: 0 0 3px 1px rgba(99, 102, 241, 0.4); }
            50% { box-shadow: 0 0 6px 2px rgba(99, 102, 241, 0.5); }
          }
          .letter-glow {
            animation: subtleGlow 1.8s infinite ease-in-out;
          }
        `}
      </style>

      {/* Header for Audio History */}
      <div className="text-center text-xs text-gray-500 font-medium mb-1">
        Audio History
      </div>

      {/* Letter Containers */}
      {lettersToDisplay.map((item, displayIndex) => {
        const feedbackClasses = getFeedbackColor(displayIndex);
        const isActive = displayIndex === activeIndex && item.letter;
        const isNMatch = displayIndex === nBack && item.letter;
        
        return (
          <div 
            key={`letter-${displayIndex}`}
            className={`
              relative flex flex-col items-center justify-center
              rounded-lg border-2 h-12 sm:h-14 
              ${feedbackClasses || 'bg-white border-gray-200 text-gray-700'}
              ${isActive ? 'shadow-md letter-glow font-bold' : 'shadow-sm font-semibold'}
              transition-all duration-150 text-center
            `}
          >
            {/* Letter Display */}
            <div className="text-lg sm:text-xl md:text-2xl uppercase">
              {item.letter || '·'}
            </div>
            
            {/* Position Label */}
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 text-xxs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[0.6rem] whitespace-nowrap">
              {getNPositionLabel(displayIndex)}
            </div>
            
            {/* N-Match Indicator */}
            {isNMatch && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-indigo-500 rounded-full border border-white"></div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AudioLetterDisplay;